import { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import {
  Button,
  Checkbox,
  Input,
  message,
  Modal,
  ModalProps,
  Pagination,
  Popconfirm,
  Select,
  Spin,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { uniq } from 'lodash-es';
import { useDebounceEffect } from 'ahooks';
import CheckContainer from 'components/CheckContainer';
import Empty from 'components/Empty';
import useProduct from './useProduct';
import Product from './Product';

const Container = styled.div`
  margin: -24px;
  padding: 14px 10px;
  .${({ theme }) => theme.getAntdPrefixedClassName('input-group-wrapper')} {
    margin-bottom: 14px;
  }
`;

const Content = styled.div`
  height: 480px;
`;

const ProductContainer = styled(CheckContainer)`
  width: 100%;
  margin-bottom: 8px;
  padding: 10px 8px;
  border-radius: 2px;
  &:hover {
    border-color: var(--ant-primary-color);
  }
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.div`
  display: flex;
  margin-left: -14px;
  > div {
    display: flex;
    align-items: center;
    height: 32px;
    padding: 0 13px;
    background: #f0f2f5;
    border-radius: 2px;
    > span {
      margin-right: 6px;
      font-size: 14px;
      font-weight: normal;
      color: #232426;
    }
    > button {
      padding: 0;
    }
  }
`;

export type Mode = 'ALL' | 'SELECTED';

export interface ProductSelectorProps extends ModalProps {
  storeId: string;
  mode: Mode;
  value: string[];
  onChange: (value: string[]) => void;
}

function ProductSelector(props: ProductSelectorProps) {
  const { storeId, mode: modeProp, value, onChange, onCancel, ...otherProps } = props;

  const { t } = useTranslate();

  const [mode, setMode] = useState(modeProp);

  const [keywords, setKeywords] = useState('');

  const [selectedList, setSelectedList] = useState(value);

  const { list, loading, page, fetchList, setList } = useProduct();

  const allChecked =
    list && list.every(({ externalItemId }) => selectedList.includes(externalItemId));

  const allIndeterminate =
    !allChecked && list && list.some(({ externalItemId }) => selectedList.includes(externalItemId));

  const refsCurrent = {
    fetchList,
    setList,
    selectedList,
    t,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  const modeOptions = useMemo(
    () => [
      {
        value: 'ALL',
        label: t('scene.chatWorkspace.productTab.status.onSale'),
      },
      {
        value: 'SELECTED',
        label: t('scene.chatWorkspace.productTab.selection.selectedProducts'),
      },
    ],
    [t],
  );

  useEffect(() => {
    setMode(modeProp);
  }, [modeProp]);

  useEffect(() => {
    setSelectedList(value);
  }, [value]);

  useDebounceEffect(() => {
    const {
      current: { fetchList, selectedList, t },
    } = refs;

    if (mode === 'SELECTED' && selectedList.length === 0) {
      fetchList(() => Promise.resolve([]));

      return;
    }

    fetchList({
      internal_store_id: storeId,
      keyword: keywords,
      page_no: 1,
      page_size: 5,
      external_item_ids: mode === 'SELECTED' ? selectedList.join(',') : undefined,
      itemStatus: mode === 'SELECTED' ? 'ALL' : undefined,
    }).catch(() => {
      message.error(t('scene.chatWorkspace.productTab.error.fetchFailed'));
    });
  }, [mode, keywords, storeId]);

  return (
    <Modal
      zIndex={1050}
      width={420}
      title={
        <Title>
          <div>
            <span>
              {t('scene.chatWorkspace.productTab.selection.selectedCount', {
                x: selectedList.length,
              })}
            </span>
            {selectedList.length > 0 && (
              <Popconfirm
                title={t('scene.chatWorkspace.productTab.selection.confirmClear')}
                onConfirm={() => setSelectedList([])}
              >
                <Button type="link">
                  {t('scene.chatWorkspace.productTab.action.clearSelected')}
                </Button>
              </Popconfirm>
            )}
          </div>
        </Title>
      }
      okText={t('scene.chatWorkspace.productTab.action.confirmSelection')}
      okButtonProps={{
        onClick: (e) => {
          onChange(selectedList);

          if (onCancel) {
            onCancel(e);
          }
        },
      }}
      onCancel={onCancel}
      {...otherProps}
    >
      <Container>
        <Input
          value={keywords}
          onChange={({ target: { value } }) => setKeywords(value)}
          allowClear
          placeholder={t('scene.chatWorkspace.productTab.placeholder.searchProduct')}
          addonBefore={<Select value={mode} options={modeOptions} onChange={setMode} />}
          suffix={<SearchOutlined />}
        />
        <Spin spinning={loading}>
          <Content>
            {!loading && (!list || list.length === 0) ? (
              <Empty />
            ) : (
              (list || []).map((product) => {
                const { externalItemId } = product;

                return (
                  <ProductContainer
                    key={externalItemId}
                    checked={selectedList.includes(externalItemId)}
                    onChange={(checked) => {
                      setSelectedList((preList) => {
                        return checked
                          ? uniq([...preList, externalItemId])
                          : preList.filter((id) => id !== externalItemId);
                      });
                    }}
                  >
                    <Product keywords={keywords} product={product} />
                  </ProductContainer>
                );
              })
            )}
          </Content>
        </Spin>
        <Toolbar>
          {list && list.length > 0 ? (
            <Checkbox
              checked={allChecked}
              indeterminate={allIndeterminate}
              onChange={({ target: { checked } }) => {
                setSelectedList((preList) => {
                  const curIdList = list.map(({ externalItemId }) => externalItemId);

                  return checked
                    ? uniq([...preList, ...curIdList])
                    : preList.filter((id) => !curIdList.includes(id));
                });
              }}
            >
              {t('scene.chatWorkspace.productTab.action.selectAllOnPage')}
            </Checkbox>
          ) : (
            <div />
          )}
          <Pagination
            simple
            {...page}
            onChange={(page, pageSize) => {
              fetchList({
                internal_store_id: storeId,
                keyword: keywords,
                page_no: page,
                page_size: pageSize,
                external_item_ids: mode === 'SELECTED' ? selectedList.join(',') : undefined,
                itemStatus: mode === 'SELECTED' ? 'ALL' : undefined,
              }).catch(() => {
                message.error(t('scene.chatWorkspace.productTab.error.fetchFailed'));
              });
            }}
          />
        </Toolbar>
      </Container>
    </Modal>
  );
}

export default ProductSelector;
